import React, { useEffect, useState, useContext, useRef } from 'react';

import styled from 'styled-components';

import { tableHelper, validateReadableId } from '@builttocreate/engine-utils';

import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FieldTypes from '../../../constants/FieldTypes';
import { swapId } from '../../../lib/utils';
import {
  getMetadataOptions,
  isSettingHidden,
} from '../../../lib/settingsHelper';
import Button from '../../misc/Button';
import Popconfirm from '../../misc/Popconfirm';
import AccordionSettings from './AccordionSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import InputSettingsField from './InputSettingsField';
import MetadataSettings from './MetadataSettings';
import OptionSettings from './OptionSettings';
import DateFormatSettings from './DateFormatSettings';
import {
  DividerLine,
  StyledButtonsWrapper,
  StyledFieldWrapper,
} from './SettingsCommonElements';

import CheckboxSettingsField from './CheckboxSettingsField';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import DecoratorSettings from './DecoratorSettings';
import SettingsLabel from './SettingsLabel';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

const { getColumnOrder } = tableHelper;

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

const FieldSettingsTableColumn = ({

  fieldId,
  fieldPositionId,
  fieldIdentifier,
  fieldType,
  columnId,

  activeFieldPosition,

  identifier,
  required,

  title,
  type,
  displayType,
  columnType,
  columnOperand,
  value,
  options,
  width,
  maxImageHeight,
  maxImageWidth,
  metadata,
  config,
  multi,

  //Action handlers
  onCapture,
  onChange,
  onPositionChange,
  onOptionCreate,
  onOptionChange,
  onOptionDelete,

  /**
   * onFieldColumnDelete is used for
   * 1.Removing the active column settings selection and hiding the column settings panel.
   * 2.Deleting the column
   */
  onFieldColumnDelete,
  onColumnIdChange,

  tableColumns,
  tableColumnOrder,
  fieldPositionTableColumns,
  fieldPositionSchema,
  schema,
  tableId,

  format, //Only used by collection fields. Table fields get format from fieldPositionTableColumns
  fields,
  useReadableIds,
  fileId,
  sortedPages,

}) => {

  const [displayConfirm, setDisplayConfirm] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(width);
  const [activeOption, setActiveOption] = useState(width === 0 ? 'auto' : 'custom');
  const [readableIdError, setReadableIdError] = useState('');

  const prevProps = useRef();

  useEffect(() => {
    
    setCurrentWidth(width);
    setActiveOption(width === 0 ? 'auto' : 'custom');

    if (prevProps.current && prevProps.current !== columnId && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = columnId

  }, [width, columnId]);

  const isTitleHidden = isSettingHidden(config, 'title');
  const isValueHidden = isSettingHidden(config, 'value');
  const isWidthHidden = isSettingHidden(config, 'width');
  const isOptionsHidden = isSettingHidden(config, 'options');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isMaxImageWidthHidden = isSettingHidden(config, 'maxImageWidth');
  const isMaxImageHeightHidden = isSettingHidden(config, 'maxImageHeight');
  const isRequiredHidden = isSettingHidden(config, 'required');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isVisibilityHidden = isSettingHidden(config, 'hiddenViews');
  const isIdHidden = isSettingHidden(config, '_id');
  const isMultiHidden = isSettingHidden(config, 'multi');
  const isColumnDecoratorsHidden = isSettingHidden(config, 'columnDecorators');

  const theme = useContext(ThemeContext);
  

  const targetFieldPositionColumn = fieldPositionTableColumns && fieldPositionTableColumns[columnId] ? fieldPositionTableColumns[columnId] : {};
  const targetFieldPositionSchema = fieldPositionSchema && fieldPositionSchema[tableId] ? fieldPositionSchema[tableId] : {};

  const targetFormat = fieldType === FieldTypes.collection ? format : targetFieldPositionColumn.format;
  const targetColumnObj = tableColumns ? tableColumns.find((col) => col._id === columnId) : {};
  const targetHidden = !!targetColumnObj?.hidden;

  const metadataOptions = getMetadataOptions(config);

  const handleWidthOption = (value, fieldType) => {

    let newWidth;

    if (value === 'auto') {
      newWidth = 0;
    } else if (value === 'custom') {
      newWidth = currentWidth;
    }

    if (newWidth !== currentWidth) {

      if (fieldType === FieldTypes.collection) {
        handleCollectionFieldColumnUpdate(columnId, { width: parseFloat(newWidth, 10) });

      } else {
        handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { width: newWidth });
      }

      setCurrentWidth(newWidth);

    }

    setActiveOption(value);

  };

  /**
   * Handler for directly updating the field position column
   */
  const handleFieldPositionColumnUpdate = (columnId, data) => {

    const existingFieldPositionTableColumns = activeFieldPosition?.fieldPositionTableColumns || {};

    const existingTargetColumn = existingFieldPositionTableColumns[columnId] || {};

    const nextFieldPositionTableColumns = {
      ...existingFieldPositionTableColumns,
      [columnId]: {
        ...existingTargetColumn,
        ...data,
      }
    };

    onPositionChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumns: nextFieldPositionTableColumns });

  };

  const handleFieldColumnUpdate = (columnId, tableColumns, tableColumnOrder, data) => {

    const nextColumns = [...tableColumns];
    const index = nextColumns.findIndex((col) => col._id === columnId);

    const payload = {};

    let updatedColumnId = columnId;

    /**
     * 1. Handle Table Field Column ID Change 
     */
    if (data && data._id) {

      updatedColumnId = data._id;
      const targetField = fields.find((field) => field._id === fieldId);

      /**
       * 1.1 Handle updating the column id
       */
      nextColumns[index] = { ...nextColumns[index], ...data };
      payload.tableColumns = nextColumns;

      /**
       * 1.2 Handle updating the column order
       */
      if (tableColumnOrder) {

        const nextColumnOrder = [...tableColumnOrder];

        //Find the index of the original column id in the column order and replace with the new column id
        const originalColumnIdIndex = nextColumnOrder.findIndex((col) => col === columnId);
        if (originalColumnIdIndex !== -1) nextColumnOrder.splice(originalColumnIdIndex, 1, data._id);

        payload.tableColumnOrder = nextColumnOrder;

      }

      /**
       * 1.3 Handle updating the row cells to reference the new column id
       */
      if (targetField && targetField.value) {

        const clonedValue = JSON.parse(JSON.stringify(targetField.value));
        const nextValue = clonedValue.map((row) => {

          if (!row.cells) return row;
          
          let nextCells = JSON.parse(JSON.stringify(row.cells));
        
          if (nextCells) {
            nextCells = swapId(nextCells, columnId, updatedColumnId);
          }

          return { ...row, cells: nextCells };

        });

         payload.value = nextValue;

      }

       /**
       * 1.4 Handle updating the fieldPositionTableColumns to reference the new column id
       */   
      if (activeFieldPosition?.fieldPositionTableColumns) {
        const nextFieldPositionTableColumns = swapId(activeFieldPosition?.fieldPositionTableColumns || {}, columnId, updatedColumnId);
        payload.fieldPositionTableColumns = nextFieldPositionTableColumns;
      }

    }
    /**
     * 2. Handle Table Field Column Delete 
     */
    else if (data && data.deleted) {

      /**
       * 2.1 Handle removing column from tableColumns
       */
      nextColumns.splice(index, 1);
      payload.tableColumns = nextColumns;

      /**
       * 2.2 Handle removing column Id from tableColumns
       */
      if (tableColumnOrder) {
        const nextColumnOrder = [...tableColumnOrder];

        //Find the index of the original column id in the column order and replace with the new column id
        const originalColumnIdIndex = nextColumnOrder.findIndex((col) => col === columnId);
        if (originalColumnIdIndex !== -1) nextColumnOrder.splice(originalColumnIdIndex, 1, data._id);

        payload.tableColumnOrder = nextColumnOrder;
      }

    }
    /**
     * 3. Handle All Other Table Field Column Updates
     */
    else {
      nextColumns[index] = { ...nextColumns[index], ...data };
      payload.tableColumns = nextColumns;
    }

    /**
     * Always pass column info as an object for consistency. If columnId is not actually 
     * changed then the oldColumnId and newColumnId will be the same.
     * 
     * What is this data for? In the JoyDoc if the columnId is changed then we use this data
     * to iterate over the fieldPositions and update the associated columnId.
     */
    const columnUpdateInfo = { oldColumnId: columnId, newColumnId: updatedColumnId };

    onChange(fieldId, fieldIdentifier, fieldPositionId, payload, columnUpdateInfo );

  };

  /**
   * Handler for directly updating the field position column
   */
  const handleCollectionFieldPositionSchemaUpdate = (columnId, change) => {

    const schemaId = tableId; //When a collection field is used, the schemaId is the tableId
    const data = activeFieldPosition?.fieldPositionSchema || {};

    const nextFieldPositionSchema = {
      ...(data || {}),
      [schemaId]: {
        ...(data[schemaId] || {}),
        tableColumns: {
          ...(data[schemaId]?.tableColumns || {}),
          [columnId]: {
            ...(data[schemaId]?.tableColumns?.[columnId] || {}),
            ...change
          }
        }
      }
    };

    onPositionChange(fieldId, fieldIdentifier, fieldPositionId, { schema: nextFieldPositionSchema });

  };

  const handleCollectionFieldColumnUpdate = (targetColumnId, data) => {

    // Ensure schema and tableId exist
    if (!schema || !schema[tableId]) return schema;

    const targetField = fields.find((field) => field._id === fieldId);

    // Clone schema and tableColumns array to avoid mutation
    const updatedSchema = { ...schema };
    const updatedTable = { ...schema[tableId] };
    updatedTable.tableColumns = [...updatedTable.tableColumns];

    // Find the column index
    const columnIndex = updatedTable.tableColumns.findIndex((col) => col._id === targetColumnId);
    if (columnIndex === -1) return schema; // Column not found, return original schema

    // Clone the column object for immutability
    const updatedColumn = { ...updatedTable.tableColumns[columnIndex] };

    // Apply other property updates
    Object.keys(data).forEach((key) => {
      if (key !== 'options') { // Avoid overriding options unintentionally
        updatedColumn[key] = data[key];
      }
    });

    // Replace the updated column back into the array
    updatedTable.tableColumns[columnIndex] = updatedColumn;

    // Replace the updated table into the schema
    updatedSchema[tableId] = updatedTable;

    let updatedColumnId = targetColumnId;
    let updatedFieldValue = targetField.value || [];

    if(data && data._id) {
      
      //Add logic to update fieldValue

      updatedColumnId = data._id.replace(/\s/g, '');

      const flattenArrayResult = flattenArray(updatedSchema, targetField.value);

      const readableIdMap = {};
      readableIdMap[targetColumnId] = updatedColumnId;

      updatedFieldValue = updateFieldValueWithReadableIds(targetField.value, flattenArrayResult, readableIdMap, tableId);


    }

    // Always pass column info as an object for consistency
    const columnUpdateInfo = { schemaId: tableId, oldColumnId: targetColumnId, newColumnId: updatedColumnId };
    onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: updatedSchema , value: updatedFieldValue}, columnUpdateInfo);

  };


const updateFieldValueWithReadableIds = (fieldValue, flattenedRows, readableIdMap, tableId) => {

  const cloneDeep = (obj) => JSON.parse(JSON.stringify(obj));

  const updatedRows = cloneDeep(fieldValue);

  for (const row of flattenedRows) {

    const { nestedPath, cells, schemaKey } = row;
    
    // Find the column key in cells that matches a key in readableIdMap
    const columnKey = Object.keys(cells).find((columnId) => readableIdMap[columnId]);
    const newKey = columnKey ? readableIdMap[columnKey] : null;
    
    if (!newKey || (newKey === columnKey && schemaKey === tableId )) continue;

    const parts = nestedPath.split('.');

    let ref = updatedRows;

    // Traverse to the row object using the nestedPath
    for (let i = 0; i < parts.length; i++) {

      const key = parts[i];

      if (i === parts.length - 1) {
      
      
        // Only update if the schemaKey matches the current tableId
        if (schemaKey === tableId && ref && ref[key] && ref[key].cells && ref[key].cells[columnKey]) {
          // Move the cell value to the new key and delete the old key
          ref[key].cells[newKey] = ref[key].cells[columnKey];
          delete ref[key].cells[columnKey];
        }
      
      } else {
        if (Array.isArray(ref)) {
          ref = ref[Number(key)];
        } else {
          ref = ref[key];
        }
        if (!ref) break;
      }
    }

  }



  return updatedRows;

}

const flattenArray = ( schema, value, oldReadableId, newReadableId) => {

  const flattenedArray = [];

  const rootSchemaKey = Object.keys(schema).find((key) => schema[key].root);

  traverseNodes(0, value, null, rootSchemaKey);

  function traverseNodes(depth, node, parentId = null, schemaKey = null, nestedPath = '', rowIndex) {

    if (!node || typeof node !== 'object') return;

    /**
     * Handle the value (rows) array
     */
    if (Array.isArray(node)) {

      // Update nestedPath for each item (using the index for uniqueness)
      node.forEach((item, index) => {

        const itemPath = nestedPath ? `${nestedPath}.${index}` : `${index}`;
      
        traverseNodes(depth, item, parentId, schemaKey, itemPath, index);
      
      });

    }

    /**
     * Handle the row obect and row children
     */
    else {

      const { _id, cells, children } = node;

      // Create a current path for this row.
      const currentPath = nestedPath ? `${nestedPath}` : _id;


      flattenedArray.push({
        _id,
        parentId,
        schemaKey,
        schema: schema[schemaKey],
        cells: cells || {},
        rowIndex,
        depth,
        nestedPath: currentPath,

       
      });

      if (children) {

        const defaultChildSchemaKeys = schema[schemaKey]?.children || [];
        const rowChildrenSchemaKeys = children ? Object.keys(children) : [];

        const mergedSchemaKeys = defaultChildSchemaKeys.concat(rowChildrenSchemaKeys);
 
        const nestedSchemaKeys = mergedSchemaKeys.filter((item, index) => mergedSchemaKeys.indexOf(item) === index); //Remove duplicates

        nestedSchemaKeys.forEach((childSchemaKey) => {

          const childData = children?.[childSchemaKey];
          const childPath = `${currentPath}.children.${childSchemaKey}.value`; // Append the child schema key to the current path

          if (childData ) {
            traverseNodes(depth + 1, childData.value, _id, childSchemaKey, childPath, undefined, cells);
          }
        });
      }

    }
  }

  return flattenedArray;

};

  const handleCollectionColumnOptionUpdate = (optionId = null, data) => {

    if (!schema || !schema[tableId]) return schema;

    // Clone schema and tableColumns array to avoid mutation
    const updatedSchema = { ...schema };
    const updatedTable = { ...updatedSchema[tableId] };
    updatedTable.tableColumns = [...updatedTable.tableColumns];

    // Find the column index
    const columnIndex = updatedTable.tableColumns.findIndex((col) => col._id === columnId);
    if (columnIndex === -1) return schema; // Column not found, return original schema

    // Clone the column object for immutability
    const updatedColumn = { ...updatedTable.tableColumns[columnIndex] };

    // Find the option index
    const optionIndex = updatedColumn.options.findIndex((opt) => opt._id === optionId);
    if (optionIndex === -1) {
      updatedColumn.options.push({...data, value: data.value || 'New Option'});
    } else {
      updatedColumn.options[optionIndex] = { ...updatedColumn.options[optionIndex], ...data };
    }

    updatedSchema[tableId].tableColumns[columnIndex].options = updatedColumn.options;

    // Pass column info as an object for consistency
    const columnUpdateInfo = { oldColumnId: columnId, newColumnId: columnId };
    onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: updatedSchema }, columnUpdateInfo);

  };

  const handleCollectionColumnOptionDelete = (optionId) => {
    if (!schema || !schema[tableId]) return schema;

    // Clone schema and tableColumns array to avoid mutation
    const updatedSchema = { ...schema };
    const updatedTable = { ...updatedSchema[tableId] };
    updatedTable.tableColumns = [...updatedTable.tableColumns];

    // Find the column index
    const columnIndex = updatedTable.tableColumns.findIndex((col) => col._id === columnId);
    if (columnIndex === -1) return schema; // Column not found, return original schema

    // Clone the column object for immutability
    const updatedColumn = { ...updatedTable.tableColumns[columnIndex] };

    // Filter out the option with the given optionId
    updatedColumn.options = updatedColumn.options.filter((opt) => opt._id !== optionId);

    // Update the schema
    updatedSchema[tableId].tableColumns[columnIndex].options = updatedColumn.options;

    // Trigger the update
    // Pass column info as an object for consistency
    const columnUpdateInfo = { oldColumnId: columnId, newColumnId: columnId };
    onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: updatedSchema }, columnUpdateInfo);
  };

  const handleCollectionTableColumnDelete = (columnId) => {

    if (!schema || !schema[tableId]) return schema;

    // Clone schema and tableColumns array to avoid mutation
    const updatedSchema = { ...schema };
    const updatedTable = { ...updatedSchema[tableId] };
    updatedTable.tableColumns = [...updatedTable.tableColumns];

    // Filter out the column with the given columnId
    updatedTable.tableColumns = updatedTable.tableColumns.filter((col) => col._id !== columnId);

    // Update the schema
    updatedSchema[tableId].tableColumns = updatedTable.tableColumns;

    // Trigger the update
    // Pass column info as an object for consistency
    const columnUpdateInfo = { oldColumnId: columnId, newColumnId: columnId };
    onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: updatedSchema }, columnUpdateInfo);

  };

  const validOptions = [];
  if (options && options.length > 0) {
    options.forEach((option) => {
      if (!option.deleted) validOptions.push(option);
    });
  }

  let defaultValueNode = null;

  if (!isValueHidden && (columnType === FieldTableColumnTypes.text || columnType === FieldTableColumnTypes.block)) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          fieldId={fieldId}
          label="Default Value"
          description="Default new row cell value"
          value={value}
          type="text"
          onChange={(i, value) => {
            if (fieldType === FieldTypes.collection) {
              handleCollectionFieldColumnUpdate(columnId, { value });
            } else {
              handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { value });
            }
          }}
        />
      </StyledFieldWrapper>
    );
  } else if (!isValueHidden && columnType === FieldTableColumnTypes.dropdown) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          type="dropdown"
          fieldId={fieldId}
          label="Default Value"
          description="Default new row cell value"
          value={value}
          options={validOptions}
          onChange={(i, value) => {
            if (fieldType === FieldTypes.collection) {
              handleCollectionFieldColumnUpdate(columnId, { value });
            }
            else {
              handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { value });
            }
          }}
        />
      </StyledFieldWrapper>
    );
  } else if (!isValueHidden && (columnType === FieldTableColumnTypes.barcode)) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          fieldId={fieldId}
          label="Default Value"
          description="Default new row cell value"
          value={value}
          type="barcode"
          onChange={(i, value) => {
            if (fieldType === FieldTypes.collection) {
              handleCollectionFieldColumnUpdate(columnId, { value });
            } else {
              handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, {
                value,
              });
            }
          }}
          onCapture={onCapture}
        />
      </StyledFieldWrapper>
    );
  } else if (!isValueHidden && columnType === FieldTableColumnTypes.multiSelect) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          type="multiselect"
          fieldId={fieldId}
          label="Default Value"
          description="Default new row cell value"
          value={value}
          options={validOptions}
          onChange={(i, value) => {
            if (fieldType === FieldTypes.collection) {
              handleCollectionFieldColumnUpdate(columnId, { value });
            } else {
              handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, {
                value,
              });
            }
          }}
        />
      </StyledFieldWrapper>
    );
  }
  else if (!isValueHidden && columnType === FieldTableColumnTypes.number) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          type="number"
          fieldId={fieldId}
          label="Default Value"
          description="Default new row cell value"
          value={value}
          options={validOptions}
          onChange={(i, value) => {
            if (fieldType === FieldTypes.collection) {
              handleCollectionFieldColumnUpdate(columnId, { value });
            } else {
              handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, {
                value,
              });
            }
          }}
        />
      </StyledFieldWrapper>
    );
  }

  let imageSettingsNode = null;

  if (columnType === FieldTableColumnTypes.image || columnType === FieldTableColumnTypes.signature) {

    const label = columnType === FieldTableColumnTypes.image ? 'Image' : 'Signature';

    imageSettingsNode = (
      <>
        {isMaxImageWidthHidden ? null : (
          <StyledFieldWrapper>
            <InputSettingsField
              fieldId={fieldId}
              id={fieldId}
              label={`${label} Width`}
              value={maxImageWidth}
              type="number"
              onChange={(i, value) => {
                if (fieldType === FieldTypes.collection) {
                  handleCollectionFieldColumnUpdate(columnId, { maxImageWidth: value });
                } else {
                  handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { maxImageWidth: value || 0 });
                }
              }}
            />
          </StyledFieldWrapper>
        )}
        {isMaxImageHeightHidden ? null : (
          <StyledFieldWrapper>
            <InputSettingsField
              fieldId={fieldId}
              id={fieldId}
              label={`${label} Height`}
              value={maxImageHeight}
              type="number"
              onChange={(i, value) => {
                if (fieldType === FieldTypes.collection) {
                  handleCollectionFieldColumnUpdate(columnId, { maxImageHeight: value });
                } else {
                  handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { maxImageHeight: value || 0 });
                } }}
            />
          </StyledFieldWrapper>
        )}
      </>
    );
  }

  let formatNode = null;

  if (columnType === FieldTableColumnTypes.date) {

    //We don't support a default value for date columns
    defaultValueNode = null; 

    formatNode = (
      <>
        <DividerLine />
        <DateFormatSettings
          format={targetFormat}
          customDateTimeFormatEnabled={config?.customDateTimeFormat}
          onChange={(value) => {
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { format: value });
              }
              else {
                handleFieldPositionColumnUpdate(columnId, { format: value });
              }
          }}
        />
      </>
    );
  }

  let requiredFieldNode = null;

  if (!isRequiredHidden && columnType !== FieldTableColumnTypes.block) {
    requiredFieldNode = (
      <>
        <DividerLine />
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.required}
            iconColor={required ? theme.dangerColor : theme.borderColor}
            iconSize="1x"
            id="required"
            label="Required"
            selected={required}
            onChange={(selected) => {
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { required: selected });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { required: selected });
              }
            }}
          />
        </StyledFieldWrapper>
      </>
    );
  }

  let hiddenFieldNode = null;
  if (!isHiddenHidden) {
    hiddenFieldNode = (
      <>
        <DividerLine />
        <SettingsLabel>Conditional Logic</SettingsLabel>
        <DividerLine style={{ marginBottom: 14 }} />
        {isLogicHidden || fields.length < 2 ? null : (
          <StyledFieldWrapper>
            <ConditionalLogicSettings
              fileId={fileId}
              targetLabel=" column"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={targetColumnObj?.logic}
              onSubmit={(nextLogic) => {
                if (fieldType === FieldTypes.collection) {
                  handleCollectionFieldColumnUpdate(columnId, { logic: nextLogic });
                } else {
                  handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { logic: nextLogic });
                }
              }}
            />
          </StyledFieldWrapper>
        )}
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.hide}
            iconColor={targetHidden ? theme.warningColor : theme.borderColor}
            iconSize="1x"
            id="hidden"
            label="Hidden"
            selected={targetHidden || false}
            onChange={(selected) => {
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { hidden: selected });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { hidden: selected });
              }
            }}
          />
        </StyledFieldWrapper>
      </>
    );

  }

  const targetColumnHiddenViews = targetColumnObj?.hiddenViews || [];

  let visibilityNode = null;
  if (!isVisibilityHidden) {
    visibilityNode = (
      <>
        <DividerLine />
        <SettingsLabel>Visibility</SettingsLabel>
        <DividerLine style={{ marginBottom: 14 }} />
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.hide}
            iconColor={targetColumnHiddenViews.includes('desktop') ? theme.warningColor : theme.borderColor}
            id="hiddenViewsDesktop"
            label="Hide on desktop"
            selected={targetColumnHiddenViews.includes('desktop')}
            onChange={(selected) => {
              const next = selected
                ? [...targetColumnHiddenViews, 'desktop']
                : targetColumnHiddenViews.filter(v => v !== 'desktop');
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { hiddenViews: next });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { hiddenViews: next });
              }
            }}
          />
        </StyledFieldWrapper>
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.hide}
            iconColor={targetColumnHiddenViews.includes('mobile') ? theme.warningColor : theme.borderColor}
            id="hiddenViewsMobile"
            label="Hide on mobile"
            selected={targetColumnHiddenViews.includes('mobile')}
            onChange={(selected) => {
              const next = selected
                ? [...targetColumnHiddenViews, 'mobile']
                : targetColumnHiddenViews.filter(v => v !== 'mobile');
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { hiddenViews: next });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { hiddenViews: next });
              }
            }}
          />
        </StyledFieldWrapper>
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.hide}
            iconColor={targetColumnHiddenViews.includes('pdf') ? theme.warningColor : theme.borderColor}
            id="hiddenViewsPdf"
            label="Hide on pdf"
            selected={targetColumnHiddenViews.includes('pdf')}
            onChange={(selected) => {
              const next = selected
                ? [...targetColumnHiddenViews, 'pdf']
                : targetColumnHiddenViews.filter(v => v !== 'pdf');
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { hiddenViews: next });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { hiddenViews: next });
              }
            }}
          />
        </StyledFieldWrapper>
      </>
    );

  }

  let multiFieldNode = null;
  if (!isMultiHidden && type === FieldTableColumnTypes.image) {
    multiFieldNode = (
      <>
        <DividerLine />
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.multiImages}
            iconColor={multi ? theme.primaryColor : theme.borderColor}
            iconSize="1x"
            id="multi"
            label="Multiple Images"
            selected={multi || false}
            onChange={(selected) => {
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { multi: selected });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { multi: selected });
              }
            }}
          />
        </StyledFieldWrapper>
      </>
    );
  }


  const readableIdNode = isIdHidden || !useReadableIds ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="ID"
        fieldId={fieldId}
        value={columnId}
        rows={3}
        error={readableIdError ? true : false}
        disabled={!useReadableIds}
        onChange={(fieldId, value) => {

        }}
        onBlur={(value) => {

          const nextValue = value.replace(/\s/g, '');

          /**
           * If the value is the same as the fieldId, don't update it and clear any errors
           */
          if (value === fieldId) {
            setReadableIdError('');
          } else if (nextValue && !validateReadableId(nextValue, null, tableColumns)) {
            setReadableIdError('Invalid ID: Must be a valid identifier and not conflict with reserved words or be a duplicate');
          } else {
            setReadableIdError('');

            if (value) {

              const nextValue = value.replace(/\s/g, ''); //Remove spaces from the new value. White spaces are not allowed in the ID.

              if(fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { _id: nextValue });
              }
              else{
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { _id: nextValue });
              }
              
              // Update the active column selection to use the new ID
              if (onColumnIdChange && nextValue !== columnId) {
                onColumnIdChange(nextValue);
              }

            }

          }

        }}
      />
      {readableIdError && (
        <StyledErrorMessage theme={theme}>
          {readableIdError}
        </StyledErrorMessage>
      )}
    </StyledFieldWrapper>
  );

  return (
    <StyledWrapper
      key={fieldId}
      data-testid={`${fieldId}-field-settings-table-column`}
    >
      <FieldSettingsHeaderTitle
        title={`${columnOperand}: Column Settings`}
        icon={ThemeIcons.fields.tableColumn}
      />
      {isTitleHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            value={title}
            label="Title"
            type="text"
            onChange={(i, value) => {
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { title: value });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, {
                  title: value,
                });
              }
            }}
          />
        </StyledFieldWrapper>
      )}
      {isWidthHidden ? null : (
        <StyledFieldWrapper>
          <ButtonGroupSettings
            label="Width"
            types={[
              {
                type: 'auto',
                label: 'Auto',
                active: activeOption === 'auto',
              },
              {
                type: 'custom',
                label: 'Custom',
                active: activeOption === 'custom',
              },
            ]}
            onChange={(value) => handleWidthOption(value, fieldType)}
          />
          {activeOption === 'custom' && (
            <InputSettingsField
              fieldId={fieldId}
              value={width}
              type="number"
              onChange={(i, value) => {
                const parsedWidth = parseFloat(value, 10);
                const nextWidth = isNaN(parsedWidth) ? 0 : parsedWidth;
                if (fieldType === FieldTypes.collection) {
                  handleCollectionFieldColumnUpdate(columnId, {
                    width: nextWidth,
                  });
                } else {
                  handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, {
                    width: nextWidth,
                  });
                }
              }}
              onClear={() => {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, {
                  width: 0,
                });
              }}
            />
          )}
        </StyledFieldWrapper>
      )}
      {defaultValueNode}
      {formatNode}
      {imageSettingsNode}
      {!isOptionsHidden &&
        (columnType === FieldTableColumnTypes.dropdown ||
          columnType === FieldTableColumnTypes.multiSelect) && (
          <StyledFieldWrapper>
            <OptionSettings
              id="options"
              config={config?.options}
              activeFieldPosition={activeFieldPosition}
              type={type}
              displayType={displayType}
              options={options}
              onChange={(optionId, data) => {
                if (fieldType === FieldTypes.collection) {
                  handleCollectionColumnOptionUpdate(optionId, data);
                } else {
                  onOptionChange(
                    fieldId,
                    fieldIdentifier,
                    fieldPositionId,
                    optionId,
                    data
                  );
                }
              }}
              onDelete={(optionId) => {
                if (fieldType === FieldTypes.collection) {
                  handleCollectionColumnOptionDelete(optionId);
                } else {
                  onOptionDelete(
                    fieldId,
                    fieldIdentifier,
                    fieldPositionId,
                    optionId
                  );
                }
              }}
              onCreate={(data) => {
                if (fieldType === FieldTypes.collection) {
                  handleCollectionColumnOptionUpdate(data._id, data);
                } else {
                  onOptionCreate(
                    fieldId,
                    fieldIdentifier,
                    fieldPositionId,
                    data
                  );
                }
              }}
              disableDragAndDrop={fieldType === FieldTypes.collection || fieldType === FieldTypes.table}
              columnType={columnType}
            />
          </StyledFieldWrapper>
      )}
      {!metadataOptions ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata
                ? { ...metadata, ...changes }
                : { ...changes };
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { metadata: nextMetadata });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, {
                  metadata: nextMetadata,
                });
              }

            }}
          />
        </>
      )}
         {readableIdNode ? (
        <>
          <DividerLine />
          {readableIdNode}
        </>
      ) : null}

      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              id={`${fieldId}-${columnId}`}
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(i, value) => {
                if (fieldType === FieldTypes.collection) {
                  handleCollectionFieldColumnUpdate(columnId, { identifier: value });
                } else {
                  handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, {
                    identifier: value,
                  });
                }
              }}
            />
          </AccordionSettings>
        </>
      )}

      {requiredFieldNode}
      {hiddenFieldNode}
      {visibilityNode}
      {multiFieldNode}
      {isColumnDecoratorsHidden ? null : (
        <>
          <SettingsLabel>Column Decorators</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <DecoratorSettings
            decorators={targetColumnObj?.decorators}
            onCreate={(data) => {
              const nextDecorators = targetColumnObj?.decorators ? [...targetColumnObj.decorators] : [];
              nextDecorators.push({
                _id: data._id,
                icon: data.icon || '',
                label: data.label || '',
                action: data.action,
                color: data.color || '',
              });
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { decorators: nextDecorators });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { decorators: nextDecorators });
              }
            }}
            onChange={(decoratorId, data) => {
              const nextDecorators = targetColumnObj?.decorators ? [...targetColumnObj.decorators] : [];
              const index = nextDecorators.findIndex((d) => d._id === decoratorId);
              if (index === -1) return;
              nextDecorators[index] = { ...nextDecorators[index], ...data };
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { decorators: nextDecorators });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { decorators: nextDecorators });
              }
            }}
            onDelete={(decoratorId) => {
              const nextDecorators = targetColumnObj?.decorators ? [...targetColumnObj.decorators] : [];
              const index = nextDecorators.findIndex((d) => d._id === decoratorId);
              if (index === -1) return;
              nextDecorators.splice(index, 1);
              if (fieldType === FieldTypes.collection) {
                handleCollectionFieldColumnUpdate(columnId, { decorators: nextDecorators });
              } else {
                handleFieldColumnUpdate(columnId, tableColumns, tableColumnOrder, { decorators: nextDecorators });
              }
            }}
          />
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => {
                setDisplayConfirm(true);
              }}
            />
            <Popconfirm
              title="Confirm Column Delete"
              description="Deletion will remove the table column and associated individual table cell fields"
              isOpen={displayConfirm}
              positions={['left']} // preferred positions by priority
              padding={18}
              cancelText="Cancel"
              onCancel={() => setDisplayConfirm(false)}
              confirmText="Confirm"
              onConfirm={() => {
                setDisplayConfirm(false);
                if (fieldType === FieldTypes.collection) {
                  handleCollectionTableColumnDelete(columnId);
                } else if (onFieldColumnDelete) { onFieldColumnDelete(
                  fieldId,
                  fieldIdentifier,
                  fieldPositionId,
                  columnId
                ); }
              }}
            >
              <div />
            </Popconfirm>
          </StyledButtonsWrapper>
        </>
      )}
    </StyledWrapper>
  );

};

export default FieldSettingsTableColumn;
